import { useQuery } from "@apollo/client";
import { Logout } from "@mui/icons-material";
import bowser from "bowser";
import _ from "lodash";
import { useSnackbar } from "notistack";
import { ReactElement, useMemo } from "react";
import { FormattedMessage } from "react-intl";
import {
  useQueryParam,
  withDefault,
  NumberParam,
  StringParam,
} from "use-query-params";

import {
  ListSessionsQuery,
  ListSessionsQueryVariables,
  EndSessionMutation,
  EndSessionMutationVariables,
  EndAllMyOtherSessionsMutation,
  EndAllMyOtherSessionsMutationVariables,
} from "~frontend/__generated__/graphql";
import ErrorCard from "~frontend/components/ErrorCard";
import Loading from "~frontend/components/Loading";
import PageContainer from "~frontend/components/PageContainer";
import Table, { TableControls, TableBox } from "~frontend/components/Table";
import {
  END_ALL_MY_OTHER_SESSIONS,
  END_SESSION,
  LIST_SESSIONS,
} from "~frontend/graphql/sessions";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import { useIntl } from "~frontend/providers/IntlProvider";
import useMutation from "~frontend/util/useMutation";

export const SESSION_SORTS = [
  {
    key: "created_at_desc",
    label: "Login Time, Newest First",
  },
  {
    key: "created_at_asc",
    label: "Login Time, Oldest First",
  },
] as const;

export type SessionSort = (typeof SESSION_SORTS)[number]["key"];

function validateSort(sort: string): SessionSort {
  return SESSION_SORTS.find(s => s.key === sort)?.key ?? SESSION_SORTS[0].key;
}

export function Sessions(): ReactElement {
  const intl = useIntl();
  const snackbar = useSnackbar();

  useBreadcrumbs([
    {
      label: intl.formatMessage({
        defaultMessage: "Active Sessions",
        description: "User sessions page header",
      }),
      path: "/user/sessions",
    },
  ]);

  const { data, loading, error } = useQuery<
    ListSessionsQuery,
    ListSessionsQueryVariables
  >(LIST_SESSIONS);

  const [endSession] = useMutation<
    EndSessionMutation,
    EndSessionMutationVariables
  >(END_SESSION, {
    onErrorSnackbarMessage: intl.formatMessage({
      defaultMessage: "Failed to end session",
      description: "Error message when failing to end a session",
    }),
    refetchQueries: [LIST_SESSIONS],
    awaitRefetchQueries: true,
  });

  const [endAllMyOtherSessions] = useMutation<
    EndAllMyOtherSessionsMutation,
    EndAllMyOtherSessionsMutationVariables
  >(END_ALL_MY_OTHER_SESSIONS, {
    onErrorSnackbarMessage: intl.formatMessage({
      defaultMessage: "Failed to log out other sessions",
      description: "Error message when failing to log out other sessions",
    }),
    refetchQueries: [LIST_SESSIONS],
    awaitRefetchQueries: true,
  });

  const [page, setPage] = useQueryParam("page", withDefault(NumberParam, 0));
  const [pageSize, setPageSize] = useQueryParam(
    "page_size",
    withDefault(NumberParam, 10)
  );
  const [sort, setSort] = useQueryParam(
    "sort",
    withDefault(StringParam, SESSION_SORTS[0].key)
  );

  const validatedSort = validateSort(sort);

  const sortedRows = useMemo(
    () =>
      _.sortBy(data?.listMySessions ?? [], s =>
        validatedSort === "created_at_desc" ? -s.createdAt : s.createdAt
      ),
    [data, validatedSort]
  );

  const pageRows = sortedRows.slice(page * pageSize, (page + 1) * pageSize);

  if (error) {
    return <ErrorCard errors={[error]} />;
  }

  async function handleLogOutAll() {
    const { errors } = await endAllMyOtherSessions();

    if (!errors) {
      snackbar.enqueueSnackbar(
        intl.formatMessage({
          defaultMessage: "Logged out all other sessions",
          description: "Snackbar message when logging out all other sessions",
        }),
        { variant: "success" }
      );
    }
  }

  async function handleLogOut(sessionId: string) {
    const { errors } = await endSession({
      variables: {
        id: sessionId,
      },
    });

    if (!errors) {
      snackbar.enqueueSnackbar(
        intl.formatMessage({
          defaultMessage: "Ended session",
          description: "Snackbar message when logging out another session",
        }),
        { variant: "success" }
      );
    }
  }

  return (
    <PageContainer maxWidth="lg">
      <TableControls
        actionButtons={
          sortedRows.find(s => !s.isCurrentSession)
            ? [
                {
                  label: (
                    <FormattedMessage
                      defaultMessage="Log Out All Other Sessions"
                      description="Button label to log out all other sessions"
                    />
                  ),
                  loadingLabel: (
                    <FormattedMessage
                      defaultMessage="Logging Out Sessions..."
                      description="Button label when logging out all other sessions"
                    />
                  ),
                  onClick: () => handleLogOutAll(),
                  id: "log-out-all",
                  confirm: {
                    title: intl.formatMessage({
                      defaultMessage: "Log Out All Other Sessions",
                      description: "Confirmation dialog title",
                    }),
                    description: intl.formatMessage({
                      defaultMessage:
                        "Are you sure you want to log out from all other devices?",
                      description: "Confirmation dialog description",
                    }),
                    confirmationText: intl.formatMessage({
                      defaultMessage: "Log Out Other Sessions",
                      description: "Confirmation dialog confirm button label",
                    }),
                    cancellationText: intl.formatMessage({
                      defaultMessage: "Cancel",
                      description: "Confirmation dialog cancel button label",
                    }),
                  },
                },
              ]
            : []
        }
        sort={{
          value: sort,
          onChange: value => setSort(value as SessionSort),
          options: SESSION_SORTS.map(sort => ({
            value: sort.key,
            label: sort.label,
          })),
        }}
      />
      <TableBox sx={{ pb: 1 }}>
        {loading ? (
          <Loading />
        ) : (
          <Table
            columns={[
              {
                key: "createdAt",
                title: (
                  <FormattedMessage
                    defaultMessage="Logged In At"
                    description="Table column header for the time the user logged in"
                  />
                ),
                renderValue: session =>
                  new Date(session.createdAt).toLocaleString(),
                align: "left",
              },
              {
                key: "browser",
                title: (
                  <FormattedMessage
                    defaultMessage="Device"
                    description="Table column header for the session's web browser name"
                  />
                ),
                renderValue: session => {
                  const browserInfo = bowser.parse(session.userAgent);
                  return (
                    <>
                      {browserInfo.browser.name ?? "Unknown Browser"} on{" "}
                      {browserInfo.os.name ?? "Unknown OS"}
                    </>
                  );
                },
                align: "left",
              },
              {
                key: "ip",
                title: (
                  <FormattedMessage
                    defaultMessage="IP Address"
                    description="Table column header for the session's IP address"
                  />
                ),
                renderValue: session => session.userIp,
                align: "left",
              },
            ]}
            rows={pageRows}
            rowActions={session =>
              session.isCurrentSession
                ? []
                : [
                    {
                      id: "logout",
                      type: "button",
                      icon: <Logout />,
                      tooltipLabel: intl.formatMessage({
                        defaultMessage: "End This Session",
                        description: "Tooltip label for ending a session",
                      }),
                      confirm: {
                        title: intl.formatMessage({
                          defaultMessage: "End Session",
                          description: "Confirmation dialog title",
                        }),
                        description: intl.formatMessage({
                          defaultMessage:
                            "Are you sure you want to log out from this device?",
                          description: "Confirmation dialog description",
                        }),
                        confirmationText: intl.formatMessage({
                          defaultMessage: "End Session",
                          description:
                            "Confirmation dialog confirm button label",
                        }),
                        cancellationText: intl.formatMessage({
                          defaultMessage: "Cancel",
                          description:
                            "Confirmation dialog cancel button label",
                        }),
                      },
                      onClick: () => handleLogOut(session.id),
                    },
                  ]
            }
            emptyState="No tenants found"
            totalCount={sortedRows.length}
            rowsPerPage={pageSize}
            onChangeRowsPerPage={newRowsPerPage => {
              setPageSize(newRowsPerPage);
              setPage(0);
            }}
            page={page}
            onChangePage={setPage}
          />
        )}
      </TableBox>
    </PageContainer>
  );
}
